import React, { useEffect, useRef, useState } from 'react';

import { Select } from '@aline/common';
import { CardContent, useTheme } from '@mui/material';
import scrollIntoView from 'smooth-scroll-into-view-if-needed';
import { CollapsibleText, SmallAlineButton } from 'src/components';
import { AlineMarkdownEditor } from 'src/components/ai/chat/ChatPanel/AlineMarkdownEditor/AlineMarkdownEditor';
import type { AlineMarkdownEditorRef } from 'src/components/ai/chat/ChatPanel/AlineMarkdownEditor/types';
import { getCommentMarkdownMentions } from 'src/components/comments/comment-markdown-mentions';
import { GrantMentionedUsersAccessDialog } from 'src/components/simple-editor/GrantMentionedUsersAccessDialog';
import { useMentionedAccountsWithoutAccessForUserIds } from 'src/components/simple-editor/hooks';
import type { MentionEntity } from 'src/components/simple-editor/types';
import { Apollo } from 'src/gql/generated';
import { useMaybeWorkflowParams } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

import type { CommentPanelComment } from '../comments/CommentsDrawer';
import { NumReplies } from '../comments/NumReplies';

const useStyles = makeStyles()(theme => ({
  button: {
    marginLeft: theme.spacing(0.5),
  },
  buttonContainer: {
    alignItems: 'center',
    display: 'flex',
    marginTop: theme.spacing(0.5),
  },
  cardContent: {
    '&:last-child': {
      paddingBottom: theme.spacing(1),
    },
    padding: theme.spacing(1),
  },
  textAreaContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(1),
  },
}));

interface CommentBoxContentProps {
  commentOrReply: CommentPanelComment;
  editing: boolean;
  isComment: boolean;
  rootCommentId: string;
  setEditing: React.Dispatch<React.SetStateAction<boolean>>;
  showReplies: boolean;
  /** Thread privacy from the root comment. Replies inherit this; do not read reply.isPrivate. */
  threadIsPrivate: boolean;
}

export function CommentBoxContent(props: CommentBoxContentProps): JSX.Element {
  const { classes } = useStyles();
  const { commentOrReply, editing, isComment, rootCommentId, showReplies } = props;

  if (!editing)
    return (
      <>
        <CardContent
          className={classes.cardContent}
          data-cy={Select.document.editor.rightDrawer.comments.commentBoxCard.content}
        >
          <CollapsibleText>
            <AlineMarkdownEditor
              noBorder
              readOnly
              maxHeight="none"
              minHeight={0}
              textareaStyle={{ padding: 0 }}
              value={commentOrReply.text}
            />
          </CollapsibleText>
        </CardContent>
        {showReplies && isComment && <NumReplies rootCommentId={rootCommentId} />}
      </>
    );

  return <CommentEditor {...props} />;
}

function CommentEditor({
  commentOrReply,
  isComment,
  rootCommentId,
  setEditing,
  threadIsPrivate,
}: CommentBoxContentProps): JSX.Element {
  const theme = useTheme();
  const { classes } = useStyles();
  const editorId = commentOrReply.id;
  const { documentId, workflowId } = useMaybeWorkflowParams();
  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const [updateComment, { loading: updatingComment }] = Apollo.useUpdateCommentMutation();
  const editorRef = useRef<AlineMarkdownEditorRef>(null);
  const [markdown, setMarkdown] = useState(commentOrReply.text);
  const [open, setOpen] = useState<boolean>(false);
  const [pendingMarkdown, setPendingMarkdown] = useState<string | null>(null);
  const [emailsWithoutAccess, setEmailsWithoutAccess] = useState<string[]>([]);
  const pristine = markdown === commentOrReply.text;

  const mentionEntity: MentionEntity = workflowId
    ? { __typename: 'Workflow', id: workflowId }
    : { __typename: 'Document', id: documentId };

  const { canEditMentionEntity, getEmailsWithoutAccessForUserIds, mentionEntityOwner } =
    useMentionedAccountsWithoutAccessForUserIds({
      isPrivate: threadIsPrivate,
      mentionEntity,
    });

  useEffect(() => {
    if (node) scrollIntoView(node, { behavior: 'smooth', scrollMode: 'if-needed' });
  }, [node]);

  async function handleSave(e: React.SyntheticEvent): Promise<void> {
    e.stopPropagation();
    if (pristine) return;

    await handleSubmitMarkdown(markdown);
  }

  async function handleSubmitMarkdown(nextMarkdown: string): Promise<void> {
    const trimmed = nextMarkdown.trim();
    if (!trimmed) return setEditing(false);

    const { directMentionEmails, mentionedUserIds: userMentionIds } = getCommentMarkdownMentions(
      editorRef.current?.getEditor(),
      trimmed
    );
    const nextEmailsWithoutAccess = getEmailsWithoutAccessForUserIds(
      userMentionIds,
      directMentionEmails
    );
    if (nextEmailsWithoutAccess.length) {
      setEmailsWithoutAccess(nextEmailsWithoutAccess);
      setPendingMarkdown(trimmed);
      setOpen(true);
      return;
    }

    await handleUpdateComment(trimmed);
  }

  async function handleUpdateComment(text: string): Promise<void> {
    // Update the comment if it's a comment, the reply if it's a reply.
    await updateComment({
      variables: {
        input: {
          id: isComment ? rootCommentId : commentOrReply.id,
          props: { text },
        },
      },
    });
    setEditing(false);
  }

  function handleCancel(e: React.MouseEvent): void {
    e.stopPropagation();
    setEditing(false);
  }

  return (
    <>
      <div ref={setNode} className={classes.textAreaContainer}>
        <AlineMarkdownEditor
          key={editorId}
          ref={editorRef}
          autoFocus
          dataCy={Select.document.editor.rightDrawer.comments.commentInput}
          maxHeight={180}
          mentions={{
            allowEmailMentions: true,
            documentId,
            enabledTypes: [Apollo.MentionEntitySearchType.User],
            workflowId,
          }}
          minHeight={72}
          style={{ backgroundColor: theme.palette.common.white, borderRadius: 3 }}
          value={commentOrReply.text}
          onChange={setMarkdown}
          onSubmit={handleSubmitMarkdown}
        />
        <div className={classes.buttonContainer}>
          <SmallAlineButton
            color="primary"
            disabled={pristine}
            loading={updatingComment}
            variant="contained"
            onClick={handleSave}
          >
            Save
          </SmallAlineButton>
          <SmallAlineButton
            className={classes.button}
            color="primary"
            variant="outlined"
            onClick={handleCancel}
          >
            Cancel
          </SmallAlineButton>
        </div>
      </div>
      <GrantMentionedUsersAccessDialog
        emailsWithoutAccess={emailsWithoutAccess}
        mentionEntity={mentionEntity}
        mentionEntityOwner={mentionEntityOwner}
        open={open}
        setOpen={setOpen}
        variant={canEditMentionEntity ? 'grant-access' : 'request-access'}
        onSubmit={() => {
          if (!pendingMarkdown) return;
          void handleUpdateComment(pendingMarkdown);
        }}
      />
    </>
  );
}
